import { useState, useEffect, useRef, useContext } from 'react';
import { getUserProfile, setUserLanguage, setUserTheme, setUserMode, setUserBirthYear, setUserGender } from '../services/api';
import { ThemeContext } from '../context/ThemeContext';
import events from '../platform/events';

/**
 * User profile data and preference updates for UserProfile.
 *
 * Fetches the profile once on mount. Each change* action calls its endpoint,
 * updates the local profile and emits userDataUpdated with the response.
 *
 * @returns {{ profile, loading, error, changeLanguage, changeTheme, changeMode, changeBirthYear, changeGender }}
 */
export const useUserProfile = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { isDarkMode, toggleTheme } = useContext(ThemeContext);

  // Use a ref to track if we've already fetched the profile
  const profileFetchedRef = useRef(false);

  useEffect(() => {
    // Only fetch the profile once
    if (profileFetchedRef.current) return;
    
    const fetchUserProfile = async () => {
      try {
        setLoading(true);
        const userData = await getUserProfile();
        setProfile(userData);
              
        // Mark that we've fetched the profile
        profileFetchedRef.current = true;
        setLoading(false);
      } catch (err) {
        console.error('Error fetching user profile:', err);
        setError('Failed to load user profile');
        setLoading(false);
      }
    };

    fetchUserProfile();
    
    // Cleanup function
    return () => {
      // Reset the ref when component unmounts
      profileFetchedRef.current = false;
    };
  }, []); // Empty dependency array - only run once on mount

  const changeLanguage = async (newLanguage) => {
    if (!profile || !profile.preferences) return;
    
    try {
      // Call the dedicated API endpoint for language change
      const response = await setUserLanguage(newLanguage);
      
      // Update local state with the new preferences
      setProfile(prevProfile => ({
        ...prevProfile,
        preferences: {
          ...prevProfile.preferences,
          language_code: newLanguage
        }
      }));

      // Emit event to update UserContext with full response data
      events.emit('userDataUpdated', {
        nickname: response.nickname,
        countryCode: response.country_code,
        languageCode: response.language_code,
        theme: response.color_palette,
        lightMode: response.color_mode
      });
      
    } catch (err) {
      console.error('Error updating language preference:', err);
      setError('Failed to update language preference');
    }
  };
  
  const changeTheme = async (newTheme) => {
    if (!profile || !profile.preferences) return;
    
    try {
      // Call the dedicated API endpoint for theme change
      const response = await setUserTheme(newTheme);
      
      // Update local state with the new preferences
      setProfile(prevProfile => ({
        ...prevProfile,
        preferences: {
          ...prevProfile.preferences,
          color_palette: newTheme
        }
      }));

      // Emit event to update UserContext with full response data
      events.emit('userDataUpdated', {
        nickname: response.nickname,
        countryCode: response.country_code,
        languageCode: response.language_code,
        theme: response.color_palette,
        lightMode: response.color_mode
      });
      
    } catch (err) {
      console.error('Error updating theme preference:', err);
      setError('Failed to update theme preference');
    }
  };
  
  // Toggle the UI light/dark mode
  const changeMode = async () => {
    if (!profile || !profile.preferences) return;
    
    const newMode = isDarkMode ? 'light' : 'dark';
    
    try {
      // Call the dedicated API endpoint for mode change
      const response = await setUserMode(newMode);
      
      // Update the UI theme toggle
      toggleTheme();
      
      // Update local state with the new preferences
      setProfile(prevProfile => ({
        ...prevProfile,
        preferences: {
          ...prevProfile.preferences,
          color_mode: newMode
        }
      }));

      // Emit event to update UserContext with full response data
      events.emit('userDataUpdated', {
        nickname: response.nickname,
        countryCode: response.country_code,
        languageCode: response.language_code,
        theme: response.color_palette,
        lightMode: response.color_mode
      });
      
    } catch (err) {
      console.error('Error updating mode preference:', err);
      setError('Failed to update mode preference');
    }
  };

  const changeBirthYear = async (newBirthYear) => {
    try {
      // Call the dedicated API endpoint for birth year change
      const response = await setUserBirthYear(newBirthYear);
      
      // Update local state with the new birth year
      setProfile(prevProfile => ({
        ...prevProfile,
        birth_year: newBirthYear
      }));

      // Emit event to update UserContext with full response data
      events.emit('userDataUpdated', {
        nickname: response.nickname,
        countryCode: response.country_code,
        languageCode: response.language_code,
        theme: response.color_palette,
        lightMode: response.color_mode,
        birthYear: response.birth_year,
        gender: response.gender
      });
      
    } catch (err) {
      console.error('Error updating birth year:', err);
      setError('Failed to update birth year');
    }
  };

  const changeGender = async (newGender) => {
    try {
      // Call the dedicated API endpoint for gender change
      const response = await setUserGender(newGender);
      
      // Update local state with the new gender
      setProfile(prevProfile => ({
        ...prevProfile,
        gender: newGender
      }));

      // Emit event to update UserContext with full response data
      events.emit('userDataUpdated', {
        nickname: response.nickname,
        countryCode: response.country_code,
        languageCode: response.language_code,
        theme: response.color_palette,
        lightMode: response.color_mode,
        birthYear: response.birth_year,
        gender: response.gender
      });
      
    } catch (err) {
      console.error('Error updating gender:', err);
      setError('Failed to update gender');
    }
  };

  return {
    profile,
    loading,
    error,
    changeLanguage,
    changeTheme,
    changeMode,
    changeBirthYear,
    changeGender,
  };
};

export default useUserProfile;
